import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

/**
 * Extracts a user-friendly error message from RTK Query errors
 *
 * This utility handles the two main types of errors that RTK Query can return:
 * - FetchBaseQueryError: API/HTTP errors with response data
 * - SerializedError: Network errors, timeouts, or other non-HTTP errors
 *
 * @param error - The error caught from an RTK Query mutation or query
 * @param fallbackMessage - Default message if no specific error is found
 * @returns User-friendly error message extracted from the error object
 * ```
 */
export function extractErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  // Type guard for FetchBaseQueryError (API errors)
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const fetchError = error as FetchBaseQueryError;
    if (fetchError.data) {
      const apiError = fetchError.data as any;
      return apiError?.detail || apiError?.message || fallbackMessage;
    }
  }
  // Type guard for SerializedError (network/other errors)
  else if (typeof error === 'object' && error !== null && 'message' in error) {
    const serializedError = error as SerializedError;
    if (serializedError.message) {
      return serializedError.message;
    }
  }

  return fallbackMessage;
}
